import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Loader from '../../../loaders';
import { Button, Col, Container, Row } from 'reactstrap';
import { Redirect, useHistory, useParams } from 'react-router-dom/cjs/react-router-dom';
import api from "../../../../services/api";
import ECCForm from './ECCForm';
import { ErrorNotification } from '../../../alerts';
import ConfirmGeneralAlert from '../../../alerts/ConfirmGeneralAlert';
import { composeDataToSend, returnToList, validate } from './ECCUtils';
import { useMsal } from '@azure/msal-react';
import _ from 'lodash';
import { roles } from '../../../../auth/roles';

const apiUrl = "employeechangeconditionv2";

export default function ECCFormSubmission() {
    const { id = 0 } = useParams();

    const [loading, setLoading] = useState(false);
    const [formLoading, setFormLoading] = useState(false);
    const [itemInEdit, setItemInEdit] = useState({});
    const [documents, setDocuments] = useState([]);
    const [validationObj, setValidationObj] = useState([]);
    const [showValidation, setShowValidation] = useState(false);
    const [popupMessageObj, setPopupMessageObj] = useState(null);
    const { instance } = useMsal();
    const user = instance.getActiveAccount();
    const history = useHistory();

    const hasFullAccess = useMemo(() => {
        return _.intersection([roles.HR, roles.Admin], user.idTokenClaims.roles).length > 0;
    }, [roles, user]);

    const hasAccess = useMemo(() => {
        return _.intersection([roles.HR, roles.Manager, roles.Admin], user.idTokenClaims.roles).length > 0;
    }, [roles, user]);

    const loadData = useCallback(() => {
        if (!id)
            return;

        setLoading(true);

        api.get(`${apiUrl}/${id}`)
            .then(response => {
                if (!response?.data)
                    return;

                setItemInEdit({
                    ...response.data,
                });
                setDocuments(response.data.eccDocuments ?? []);
            })
            .catch((error) => console.error({error}))
            .finally(() => {
                setLoading(false);
            })
        ;
    }, [id]);

    useEffect(() => {
        if (!id)
            return;

        loadData();
    }, [id]);

    const scrollToValidation = useCallback(() => {
        if (!validationObj?.length)
            return;

        setTimeout(function () { //Start the timer
            const scrollObject = document.getElementById("validationAlert");
            if (!scrollObject) return;
            scrollObject.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }.bind(this), 500);
    }, [validationObj]);

    useEffect(() => {
        if (!showValidation)
            return;

        setShowValidation(false);
        scrollToValidation();
    }, [showValidation]);

    const handleSave = useCallback((readyForApproval = false) => {
        setLoading(true);
        const validationResult = validate(itemInEdit, documents, readyForApproval);
        setValidationObj([...validationResult]);

        if (validationResult?.length)
        {
            setShowValidation(true);
            setLoading(false);
            return;
        }
        
        const url = `${process.env.REACT_APP_WEB_API_BASE_URI}/${apiUrl}${id ? `/${id}` : ""}?readyForApproval=${readyForApproval}`;
        const method = id ? "PUT" : "POST";
        const save = api({ url, method, data: composeDataToSend(id, itemInEdit, documents) });
        
        save
            .then(() => {
                setLoading(false);

                if (!hasFullAccess)
                {
                    history.push("/");
                    return;
                }

                returnToList(history);
            })
            .catch(error => {
                console.error({error});
                setPopupMessageObj({
                    type: "error",
                    message: `${error.response.data} Your data has not been saved.`,
                });
                setLoading(false);
            })
        ;
    }, [id, itemInEdit, documents, history, hasFullAccess]);

    const handleDelete = useCallback(() => {
        if (!id)
            return;

        setLoading(true);
        
        api.delete(`${apiUrl}/${id}`)
            .then(() => {
                setLoading(false);

                if (!hasFullAccess)
                {
                    history.push("/");
                    return;
                }

                returnToList(history);
            })
            .catch(error => {
                console.error({error});
                setPopupMessageObj({
                    type: "error",
                    message: `${error.response.data} Your data has not been removed.`,
                });
                setLoading(false);
            })
        ;
    }, [id, history, hasFullAccess]);

    const footer = useMemo(() => {
        return (
            <Row className="m-0 p-0 mt-3">
                <Col xs={12} className="d-flex justify-content-between m-0 p-0 mb-4">
                    <Row className="w-100 m-0 p-0">
                        <Col xs={3} className="d-flex flex-wrap justify-content-start align-items-center m-0 p-0">
                            <Button
                                color="info"
                                onClick={() => {
                                    if (!hasFullAccess)
                                    {
                                        history.push("/");
                                        return;
                                    }

                                    returnToList(history);
                                }}
                                type="button"
                            >
                                Back
                            </Button>
                        </Col>
                        <Col xs={9} className="d-flex flex-wrap justify-content-end align-items-center m-0 p-0">
                        {
                            itemInEdit?.employeeChangeConditionId && itemInEdit.statusName === "Draft" ?
                            <Button
                                color="danger"
                                onClick={() => {
                                    setPopupMessageObj({
                                        type: "removal",
                                        message: `Are you sure you want to remove this ECCF?`,
                                    });
                                }}
                                type="button"
                            >
                                Delete
                            </Button>
                            :
                            null
                        }
                        {
                            !itemInEdit?.employeeChangeConditionId || itemInEdit.statusName === "Draft" ?
                            <>
                                <Button
                                    color="default"
                                    onClick={() => handleSave()}
                                    type="button"
                                >
                                    Save
                                </Button>
                                <Button
                                    color="default"
                                    onClick={() => {
                                        setLoading(true);
                                        const validationResult = validate(itemInEdit, documents, true);
                                        setValidationObj([...validationResult]);

                                        if (validationResult?.length)
                                        {
                                            setShowValidation(true);
                                            setLoading(false);
                                            return;
                                        }

                                        setLoading(false);
                                        setPopupMessageObj({
                                            type: "confirmation",
                                            message: "Are you sure you want to submit this request for approval?",
                                        });
                                    }}
                                    type="button"
                                >
                                    Submit for Approval
                                </Button>
                            </>
                            :
                            null
                        }
                        </Col>
                    </Row>
                </Col>
            </Row>
        );
    }, [itemInEdit, history, hasFullAccess]);

    if (!hasAccess)
        return (
            <Redirect
                to={{
                    pathname: '/unauthorized',
                    state: {
                        from: "eccform"
                    }
                }}
            />
        );

    return (
        <section className="main">
            {(loading || formLoading) && <Loader />}
            <Container fluid>
                <ECCForm
                    isApproval={false}
                    itemInEdit={itemInEdit}
                    setItemInEdit={setItemInEdit}
                    documents={documents}
                    setDocuments={setDocuments}
                    setValidationObj={setValidationObj}
                    validationObj={validationObj}
                    loading={formLoading}
                    setLoading={setFormLoading}
                    footer={footer}
                />
            </Container>
        {
            popupMessageObj?.type === "confirmation" ?
            <ConfirmGeneralAlert
                onConfirm={() => handleSave(true)}
                onCancel={() => setPopupMessageObj(null)}
                textConfirm="Yes"
                title="ECC Submit for Approval Confirmation"
                textDesc={popupMessageObj.message}
            />
            :
            null
        }
        {
            popupMessageObj?.type === "removal" ?
            <ConfirmGeneralAlert
                onConfirm={() => handleDelete()}
                onCancel={() => setPopupMessageObj(null)}
                textConfirm="Yes"
                title="ECC Delete Confirmation"
                textDesc={popupMessageObj.message}
            />
            :
            null
        }
        {
            popupMessageObj?.type === "error" ?
            <ErrorNotification
                onConfirm={() => setPopupMessageObj(null)}
                message={popupMessageObj.message}
            />
            :
            null
        }
        </section>
    );
};